import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';
import type {
    ChartBlock,
    LineChartSpec,
    SvgChartSpec,
} from '../api/aiSearchTypes';

// ---------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------

// Fallback palette when the backend doesn't specify a colour per series.
const DEFAULT_COLORS = [
    '#4fc3f7',
    '#81c784',
    '#ffb74d',
    '#f06292',
    '#ba68c8',
    '#4db6ac',
];

function seriesColor(color: string | undefined, idx: number): string {
    return color ?? DEFAULT_COLORS[idx % DEFAULT_COLORS.length];
}

// ---------------------------------------------------------------------------
// Shared chart container
// ---------------------------------------------------------------------------

function ChartShell({
    title,
    height = 260,
    children,
}: {
    title?: string;
    height?: number | 'auto';
    children: React.ReactNode;
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: '6px',
                overflow: 'hidden',
            }}
        >
            {title && (
                <Box
                    sx={{
                        px: 1.5,
                        py: 0.875,
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: isDark
                            ? alpha('#fff', 0.02)
                            : alpha('#000', 0.02),
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                    >
                        {title}
                    </Typography>
                </Box>
            )}
            <Box sx={{ height, px: 1, pt: 1.5, pb: 0.5 }}>{children}</Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------

function ChartTooltip({
    active,
    payload,
    label,
    xType,
}: {
    active?: boolean;
    payload?: { name: string; value: number; color: string }[];
    label?: string;
    xType: LineChartSpec['x_type'];
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    if (!active || !payload?.length) return null;

    const displayLabel =
        xType === 'time' && label
            ? new Date(label).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
              })
            : label;

    return (
        <Box
            sx={{
                bgcolor: isDark ? '#1e1e1e' : '#fff',
                border: 1,
                borderColor: 'divider',
                borderRadius: '4px',
                px: 1.25,
                py: 0.875,
                boxShadow: 2,
            }}
        >
            <Typography
                sx={{ fontSize: '10.5px', color: 'text.disabled', mb: 0.5 }}
            >
                {displayLabel}
            </Typography>
            {payload.map((p, i) => (
                <Box
                    key={i}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                >
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: p.color,
                            flexShrink: 0,
                        }}
                    />
                    <Typography
                        sx={{ fontSize: '11px', color: 'text.secondary' }}
                    >
                        {p.name}:
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '11px',
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                    >
                        {typeof p.value === 'number'
                            ? p.value % 1 === 0
                                ? p.value
                                : p.value.toFixed(2)
                            : p.value}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Line chart renderer
// ---------------------------------------------------------------------------

function LineChartView({ spec }: { spec: LineChartSpec }) {
    const theme = useTheme();

    const tickFormatter = useMemo(() => {
        if (spec.x_type !== 'time') return undefined;
        return (val: string) =>
            new Date(val).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
            });
    }, [spec.x_type]);

    const yDomain = (spec.y_domain as
        | [number | string, number | string]
        | undefined) ?? ['auto', 'auto'];

    return (
        <ChartShell title={spec.title} height={260}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={spec.data as Record<string, unknown>[]}
                    margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
                >
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={
                            theme.palette.mode === 'dark'
                                ? alpha('#fff', 0.06)
                                : alpha('#000', 0.06)
                        }
                        vertical={false}
                    />
                    <XAxis
                        dataKey={spec.x_key}
                        tickFormatter={tickFormatter}
                        tick={{
                            fontSize: 10,
                            fill: theme.palette.text.disabled as string,
                        }}
                        axisLine={false}
                        tickLine={false}
                        minTickGap={40}
                    />
                    <YAxis
                        domain={yDomain}
                        tick={{
                            fontSize: 10,
                            fill: theme.palette.text.disabled as string,
                        }}
                        axisLine={false}
                        tickLine={false}
                        width={36}
                        label={
                            spec.y_label
                                ? {
                                      value: spec.y_label,
                                      angle: -90,
                                      position: 'insideLeft',
                                      style: {
                                          fontSize: 10,
                                          fill: theme.palette.text
                                              .disabled as string,
                                      },
                                      offset: 8,
                                  }
                                : undefined
                        }
                    />
                    <Tooltip
                        content={<ChartTooltip xType={spec.x_type} />}
                        cursor={{
                            stroke: theme.palette.divider,
                            strokeWidth: 1,
                        }}
                    />
                    {spec.series.length > 1 && (
                        <Legend
                            iconType="circle"
                            iconSize={7}
                            wrapperStyle={{ fontSize: 10, paddingTop: 4 }}
                        />
                    )}
                    {spec.series.map((s, i) => (
                        <Line
                            key={s.key}
                            type="monotone"
                            dataKey={s.key}
                            name={s.label}
                            stroke={seriesColor(s.color, i)}
                            strokeWidth={1.5}
                            dot={false}
                            activeDot={{ r: 3, strokeWidth: 0 }}
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </ChartShell>
    );
}

// ---------------------------------------------------------------------------
// SVG renderer
// ---------------------------------------------------------------------------

function SvgChartView({ spec }: { spec: SvgChartSpec }) {
    return (
        <ChartShell title={spec.title} height="auto">
            <Box
                // dangerouslySetInnerHTML is appropriate here: the SVG comes
                // from our own backend, not user input, and is same-origin.
                dangerouslySetInnerHTML={{ __html: spec.svg }}
                sx={{
                    '& svg': {
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                    },
                }}
            />
        </ChartShell>
    );
}

// ---------------------------------------------------------------------------
// Public renderer — used by ConversationThread's BlockRenderer
// ---------------------------------------------------------------------------

export function AIChartBlock({ block }: { block: ChartBlock }) {
    switch (block.chart.format) {
        case 'line_chart':
            return <LineChartView spec={block.chart} />;
        case 'svg':
            return <SvgChartView spec={block.chart} />;
    }
}
